import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import DeckglBase from "./DeckglBase";
import "mapbox-gl/dist/mapbox-gl.css";
import * as turf from '@turf/turf';
import RBush from 'rbush';

import { cityIOSettings } from "../../../settings/settings";
import { GeoJsonLayer, HeatmapLayer, IconLayer, PathLayer, ScatterplotLayer} from "deck.gl";
import { PathStyleExtension } from '@deck.gl/extensions';
import { DataFilterExtension } from '@deck.gl/extensions';
import { WebMercatorViewport } from '@deck.gl/core';

import { evaluate } from "./colormaps";
import StatsPanel from "../../../Components/StatsPanel";
import { updateLayerData } from "../../../redux/reducers/dataSlice";

export default function DeckGLMap() {
  const [deckGLRef, setDeckGLRef] = useState(null);
  const [hoveredFoco, setHoveredFoco] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null); // Para albergues, alimentación, acopios
  const [zoomLevel, setZoomLevel] = useState(9);
  const [currentViewState, setCurrentViewState] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const previousFocoRef = useRef(null);
  const viewportDebounceRef = useRef(null);
  const lastBboxRef = useRef(null);

  const dispatch = useDispatch();
  const layers = useSelector((state) => state.dataState.layers);
  const timeRange = useSelector((state) => state.dataState.timeRange);

  // Función para calcular bbox desde el viewState
  const getBboxFromViewState = useCallback((viewState) => {
    if (!viewState || !viewState.width || !viewState.height) return null;

    try {
      const viewport = new WebMercatorViewport(viewState);
      const nw = viewport.unproject([0, 0]);
      const se = viewport.unproject([viewState.width, viewState.height]);

      // Expandir bbox un 20% para precargar áreas cercanas
      const expandFactor = 0.2;
      const width = se[0] - nw[0];
      const height = nw[1] - se[1];

      return {
        minX: nw[0] - width * expandFactor,
        minY: se[1] - height * expandFactor,
        maxX: se[0] + width * expandFactor,
        maxY: nw[1] + height * expandFactor
      };
    } catch (e) {
      console.error('Error calculating bbox:', e);
      return null;
    }
  }, []);

  // Función para recargar layers que soportan bbox
  const reloadLayersWithBbox = useCallback(async (bbox, currentZoom) => {
    if (!bbox) return;

    const bboxString = `${bbox.minX.toFixed(4)},${bbox.minY.toFixed(4)},${bbox.maxX.toFixed(4)},${bbox.maxY.toFixed(4)}`;

    // Evitar recargar si el bbox es muy similar al anterior
    if (lastBboxRef.current === bboxString) return;
    lastBboxRef.current = bboxString;

    // Recargar solo layers que soportan bbox y están visibles
    const layersToReload = layers.filter(l => l.supportsBbox && l.visible && l.baseUrl);

    for (const layer of layersToReload) {
      try {
        const simplifyParam = currentZoom <= 11 ? '&simplify=0.001' : '';
        // const url = `${layer.baseUrl}?bbox=${bboxString}${simplifyParam}`;
        const url = `${layer.baseUrl}?bbox=${bboxString}${simplifyParam}`;

        const res = await fetch(url);
        const data = await res.json();
        dispatch(updateLayerData({ layerId: layer.id, data }));
      } catch (error) {
        console.error(`Error reloading layer ${layer.id}:`, error);
      }
    }
  }, [layers, dispatch]);

  // Efecto para recargar layers cuando cambia el viewport (con debounce)
  useEffect(() => {
    if (!currentViewState) return;

    // Limpiar timeout anterior
    if (viewportDebounceRef.current) {
      clearTimeout(viewportDebounceRef.current);
    }

    // Siempre recargar con bbox cuando el usuario mueve el mapa
    viewportDebounceRef.current = setTimeout(() => {
      const bbox = getBboxFromViewState(currentViewState);
      reloadLayersWithBbox(bbox, zoomLevel);
    }, 300); // Esperar 300ms después de dejar de mover el mapa

    return () => {
      if (viewportDebounceRef.current) {
        clearTimeout(viewportDebounceRef.current);
      }
    };
  }, [currentViewState, zoomLevel, getBboxFromViewState, reloadLayersWithBbox]);

  // Índice espacial R-tree para búsqueda ultra-rápida
  const blocksIndex = useMemo(() => {
    const blocksLayer = layers.find(l => l.id === 'blocks');
    if (!blocksLayer?.data?.features) return null;

    const tree = new RBush();
    const items = blocksLayer.data.features.map((feature, idx) => {
      const bbox = turf.bbox(feature);
      return {
        minX: bbox[0],
        minY: bbox[1],
        maxX: bbox[2],
        maxY: bbox[3],
        feature: feature,
        centroid: turf.centroid(feature),
        idx: idx
      };
    });
    
    tree.load(items);
    return tree;
  }, [layers]);

  // // Caché de geometrías simplificadas (70% menos vértices)
  // const simplifiedBlocks = useMemo(() => {
  //   const blocksLayer = layers.find(l => l.id === 'blocks');
  //   if (!blocksLayer?.data?.features) return null;

  //   return blocksLayer.data.features.map(feature => {
  //     try {
  //       return turf.simplify(feature, { tolerance: 0.00001, highQuality: false });
  //     } catch (e) {
  //       return feature;
  //     }
  //   });
  // }, [layers]);

  // Calcular estadísticas de bloques afectados (memoizado para evitar re-renders)
  const affectedStats = useMemo(() => {
    if (!hoveredFoco) return null;

    try {
      const focoCoords = hoveredFoco.geometry.coordinates;
      
      // Calcular focos cercanos (dentro de 1 km) - siempre se muestra
      const focosLayer = layers.find(l => l.id === 'focos');
      let nearbyFocosCount = 0;
      if (focosLayer?.data?.features) {
        nearbyFocosCount = focosLayer.data.features.filter(f => {
          if (f === hoveredFoco) return false; // Excluir el foco actual
          const distance = turf.distance(
            focoCoords,
            f.geometry.coordinates,
            { units: 'kilometers' }
          );
          return distance <= 1;
        }).length;
      }
      
      // Datos base que siempre se muestran
      const baseStats = {
        population: 0,
        blocksCount: 0,
        area: 0,
        avgDensity: 0,
        nearbyFocos: nearbyFocosCount,
        intersectedBlocks: []
      };
      
      // Si hay índice de bloques, calcular intersecciones
      const blocksLayer = layers.find(l => l.id === 'blocks');
      if (blocksIndex && blocksLayer.data) {
        const radiusKm = 1;
        
        const radiusDegrees = radiusKm / 111;
        const searchBox = {
          minX: focoCoords[0] - radiusDegrees,
          minY: focoCoords[1] - radiusDegrees,
          maxX: focoCoords[0] + radiusDegrees,
          maxY: focoCoords[1] + radiusDegrees
        };
        
        const candidates = blocksIndex.search(searchBox);
        
        const closeBlocks = candidates.filter(item => {
          const distance = turf.distance(
            focoCoords,
            item.centroid.geometry.coordinates,
            { units: 'kilometers' }
          );
          return distance <= radiusKm;
        });
        
        if (closeBlocks.length > 0) {
          const focoBuffer = turf.buffer(hoveredFoco, radiusKm, { units: 'kilometers', steps: 8 });
          
          const intersectedBlocks = closeBlocks.filter(item => {
            try {
              const simplified = blocksLayer.data.features[item.idx];
              return turf.booleanIntersects(simplified, focoBuffer);
            } catch (e) {
              return false;
            }
          }).map(item => item.feature);
          
          if (intersectedBlocks.length > 0) {
            const totalPopulation = intersectedBlocks.reduce((sum, block) => {
              return sum + (block.properties.density || 0);
            }, 0);
            
            const totalAreaHectares = intersectedBlocks.reduce((sum, block) => {
              try {
                return sum + turf.area(block) / 10000; // Convertir m² a hectáreas
              } catch(e) {
                return sum;
              }
            }, 0);
            
            const avgDensity = totalPopulation / (totalAreaHectares / 100); // Densidad por km² (1 km² = 100 ha)
            
            return {
              population: Math.round(totalPopulation),
              blocksCount: intersectedBlocks.length,
              area: totalAreaHectares,
              avgDensity: avgDensity,
              nearbyFocos: nearbyFocosCount,
              intersectedBlocks: intersectedBlocks
            };
          }
        }
      }
      
      // Retornar datos base si no hay intersecciones
      return baseStats;
      
    } catch (e) {
      console.error('Error calculating stats:', e);
    }
    
    return null;
  }, [hoveredFoco, blocksIndex, layers]);

  function hexToRgb(hex) {
    return hex.toLowerCase().match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16));
  }

  // Memorizar el filtrado de focos históricos para mejorar el rendimiento
  const filteredFocosHistoricos = useMemo(() => {
    const focosHistoricosLayer = layers.find(l => l.id === 'focosHistoricos');
    if (!focosHistoricosLayer?.data?.features) return [];
    
    return focosHistoricosLayer.data.features.filter(feature => {
      const acqDate = feature.properties.acq_date;
      if (!acqDate) return false;
      
      const day = parseInt(acqDate.split('-')[2], 10);
      return day >= timeRange.startDate && day <= timeRange.endDate;
    });
  }, [layers, timeRange.startDate, timeRange.endDate]);

  const renderDeckLayers = () => {
    const newLayers = [];

    // Manzanas
    const blocksLayer = layers.find(l => l.id === 'blocks');
    if(blocksLayer.data) {
      newLayers.push(new GeoJsonLayer({
        id: 'blocks',
        data: blocksLayer.data,
        filled: true,
        stroked: true,
        pickable: true,
        getFillColor: d => {
          const color = evaluate(d.properties.density / 400.0, 'BuPu', false);
          // Usar opacidad 255 si está en hover, sino 160
          const isHovered = hoveredElement?.type === 'manzana' && 
                           hoveredElement?.data === d.properties;
          return [...color.slice(0, 3), isHovered ? 255 : 215];
        },
        getLineColor: d => {
          const isHovered = hoveredElement?.type === 'manzana' && 
                           hoveredElement?.data === d.properties;
          if (isHovered) {
            return [255, 100, 0, 255];
          }
          return [0, 0, 0, 0]; // Transparente cuando no está en hover
        },
        getLineWidth: d => {
          const isHovered = hoveredElement?.type === 'manzana' && 
                           hoveredElement?.data === d.properties;
          return isHovered ? 3 : 0;
        },
        lineWidthUnits: 'pixels',
        visible: blocksLayer.visible,
        autoHighlight: false,
        updateTriggers: {
          getFillColor: [hoveredElement],
          getLineColor: [hoveredElement],
          getLineWidth: [hoveredElement]
        },
        onHover: (info) => {
          if (info.object) {
            setHoveredElement({
              type: 'manzana',
              data: info.object.properties
            });
            setHoveredFoco(null);
          } else if (!info.object && hoveredElement?.type === 'manzana') {
            setHoveredElement(null);
          }
          return false;
        }
      }));
      
      // Manzanas intersectadas con foco en hover
      if (affectedStats?.intersectedBlocks) {
        const blocksLayer = layers.find(l => l.id === 'blocks');
        newLayers.push(new GeoJsonLayer({
          id: 'blocks-intersected',
          data: turf.featureCollection(affectedStats.intersectedBlocks),
          filled: true,
          stroked: true,
          getFillColor: d => {
            const color = evaluate(d.properties.density / 250.0, 'YlOrRd', false);
            return [...color.slice(0, 3), 255]; // Alta opacidad
          },
          lineWidthMinPixels: 2,
          getLineColor: [255, 100, 0, 255],
          visible: blocksLayer.visible,
        }));
      }
    }

    // Focos históricos
    const focosHistoricosLayer = layers.find(l => l.id === 'focosHistoricos')
    if(focosHistoricosLayer?.data && filteredFocosHistoricos.length > 0) {

      // newLayers.push(new ScatterplotLayer({
      //   id: 'focosHistoricos-scatter',
      //   data: filteredFeatures,
      //   pickable: false,
      //   getPosition: d => d.geometry.coordinates,
      //   getRadius: d => (d.properties.brightness - 300.0) * 5,
      //   filled: true,
      //   stroked: true,
      //   getFillColor: d => {
      //     const color = evaluate((d.properties.brightness - 300.0) / 100.0 || 0, 'YlOrRd', false);
      //     // return color;
      //     return [...color.slice(0, 3), 32];
      //   },
      //   getLineColor: [255, 255, 255],
      //   parameters: {
      //     depthTest: false
      //   },
      //   visible: focosHistoricosLayer.visible,
      // }));

      newLayers.push(new HeatmapLayer({
        id: 'focosHistoricos-heatmap',
        data: filteredFocosHistoricos,
        getPosition: d => d.geometry.coordinates,
        getWeight: d => (d.properties.brightness - 300.0) / 100.0 || 0,
        // colorRange: [
        //   [255, 0, 0]
        // ],
        radiusPixels: 50,
        intensity: 1,
        threshold: 0.05,
        visible: focosHistoricosLayer.visible,
        opacity: 0.75,
      }));
    }

    // Jams
    const jamsLayer = layers.find(l => l.id === 'jams');
    if(jamsLayer.data) {
      newLayers.push(new GeoJsonLayer({
        id: 'jams',
        data: jamsLayer.data,
        filled: false,
        stroked: true,
        pickable: true,
        lineJointRounded: true,
        lineCapRounded: true,
        getLineColor: d => {
          // const color = evaluate(d.properties.speed / 40.0, 'Reds', true);
          // Usar opacidad 255 si está en hover, sino 160
          // const isHovered = hoveredElement?.type === 'jam' && 
          //                  hoveredElement?.data === d.properties;
          // return [...color.slice(0, 3), isHovered ? 255 : 255];
          let v = 255 * d.properties.speed / 20.0;
          if(v > 255)
            v = 255;
          if(v < 0)
            v = 0;
          return [255, v, v, 255];
        },
        getLineWidth: d => {
          const isHovered = hoveredElement?.type === 'jam' && 
                           hoveredElement?.data === d.properties;
          return isHovered ? 12 : 6;
        },
        lineWidthUnits: 'pixels',
        visible: jamsLayer.visible,
        autoHighlight: false,
        updateTriggers: {
          getFillColor: [hoveredElement],
          getLineColor: [hoveredElement],
          getLineWidth: [hoveredElement]
        },
        onHover: (info) => {
          if (info.object) {
            setHoveredElement({
              type: 'jam',
              data: info.object.properties
            });
            setHoveredFoco(null);
          } else if (!info.object && hoveredElement?.type === 'jam') {
            setHoveredElement(null);
          }
          return false;
        }
      }));
    }

    // Albergues
    const alberguesLayer = layers.find(l => l.id === 'albergues')
    if(alberguesLayer?.data) {
      newLayers.push(new IconLayer({
        id: 'albergues-pin',
        data: alberguesLayer.data['features'],
        pickable: true,
        iconAtlas: cityIOSettings.iconsURL + '/pin_house.png',
        iconMapping: {
          marker: { x: 0, y: 0, width: 128, height: 128, mask: true }
        },
        getIcon: d => 'marker',
        sizeScale: d => hoveredElement?.type === 'albergue' && 
                   hoveredElement?.data === d.properties ? 1.3 : 1,
        sizeMinPixels: 32,
        sizeMaxPixels: 32,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 203, 5],
        parameters: {
          depthTest: false
        },
        visible: alberguesLayer.visible,
        autoHighlight: false,
        onHover: (info) => {
          if (info.object) {
            setHoveredElement({
              type: 'albergue',
              data: info.object.properties
            });
            setHoveredFoco(null);
          } else {
            setHoveredElement(null);
          }
          return false;
        }
      }));
      newLayers.push(new IconLayer({
        id: 'albergues_icon',
        data: alberguesLayer.data['features'],
        iconAtlas: cityIOSettings.iconsURL + '/pin_house.png',
        iconMapping: {
          marker: { x: 128, y: 0, width: 128, height: 128, mask: true }
        },
        getPixelOffset: [0, -3],
        getIcon: d => 'marker',
        sizeMinPixels: 16,
        sizeMaxPixels: 16,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 255, 255],
        parameters: {
          depthTest: false
        },
        visible: alberguesLayer.visible
      }));
    }

    // Alimentacion
    const alimentacionLayer = layers.find(l => l.id === 'alimentacion')
    if(alimentacionLayer?.data) {
      newLayers.push(new IconLayer({
        id: 'alimentacion-pin',
        data: alimentacionLayer.data['features'],
        pickable: true,
        iconAtlas: cityIOSettings.iconsURL + '/pin_food.png',
        iconMapping: {
          marker: { x: 0, y: 0, width: 128, height: 128, mask: true }
        },
        getIcon: d => 'marker',
        sizeScale: d => hoveredElement?.type === 'alimentacion' && 
                   hoveredElement?.data === d.properties ? 1.3 : 1,
        sizeMinPixels: 32,
        sizeMaxPixels: 32,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 128, 128],
        parameters: {
          depthTest: false
        },
        visible: alimentacionLayer.visible,
        autoHighlight: false,
        onHover: (info) => {
          if (info.object) {
            setHoveredElement({
              type: 'alimentacion',
              data: info.object.properties
            });
            setHoveredFoco(null);
          } else {
            setHoveredElement(null);
          }
          return false;
        }
      }));
      newLayers.push(new IconLayer({
        id: 'alimentacion_icon',
        data: alimentacionLayer.data['features'],
        iconAtlas: cityIOSettings.iconsURL + '/pin_food.png',
        iconMapping: {
          marker: { x: 128, y: 0, width: 128, height: 128, mask: true }
        },
        getPixelOffset: [0, -3],
        getIcon: d => 'marker',
        sizeMinPixels: 16,
        sizeMaxPixels: 16,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 255, 255],
        parameters: {
          depthTest: false
        },
        visible: alimentacionLayer.visible
      }));
    }

    // Acopios
    const acopiosLayer = layers.find(l => l.id === 'acopios')
    if(acopiosLayer?.data) {
      newLayers.push(new IconLayer({
        id: 'acopios-pin',
        data: acopiosLayer.data['features'],
        pickable: true,
        iconAtlas: cityIOSettings.iconsURL + '/pin_box.png',
        iconMapping: {
          marker: { x: 0, y: 0, width: 128, height: 128, mask: true }
        },
        getIcon: d => 'marker',
        sizeScale: d => hoveredElement?.type === 'acopio' && 
                   hoveredElement?.data === d.properties ? 1.3 : 1,
        sizeMinPixels: 32,
        sizeMaxPixels: 32,
        getPosition: d => d.geometry.coordinates,
        getColor: [128, 64, 0],
        parameters: {
          depthTest: false
        },
        visible: acopiosLayer.visible,
        autoHighlight: false,
        onHover: (info) => {
          if (info.object) {
            setHoveredElement({
              type: 'acopio',
              data: info.object.properties
            });
            setHoveredFoco(null);
          } else {
            setHoveredElement(null);
          }
          return false;
        }
      }));
      newLayers.push(new IconLayer({
        id: 'acopios_icon',
        data: acopiosLayer.data['features'],
        iconAtlas: cityIOSettings.iconsURL + '/pin_box.png',
        iconMapping: {
          marker: { x: 128, y: 0, width: 128, height: 128, mask: true }
        },
        getPixelOffset: [0, -3],
        getIcon: d => 'marker',
        sizeMinPixels: 16,
        sizeMaxPixels: 16,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 255, 255],
        parameters: {
          depthTest: false
        },
        visible: acopiosLayer.visible
      }));
    }

    // Educación
    const educacionLayer = layers.find(l => l.id === 'educacion')
    if(educacionLayer?.data) {
      newLayers.push(new IconLayer({
        id: 'educacion-pin',
        data: educacionLayer.data['features'],
        pickable: true,
        iconAtlas: cityIOSettings.iconsURL + '/pin_cap.png',
        iconMapping: {
          marker: { x: 0, y: 0, width: 128, height: 128, mask: true }
        },
        getIcon: d => 'marker',
        sizeScale: d => hoveredElement?.type === 'educacion' && 
                   hoveredElement?.data === d.properties ? 1.3 : 1,
        sizeMinPixels: 32,
        sizeMaxPixels: 32,
        getPosition: d => d.geometry.coordinates,
        getColor: [80, 80, 225],
        parameters: {
          depthTest: false
        },
        visible: educacionLayer.visible,
        autoHighlight: false,
        onHover: (info) => {
          if (info.object) {
            setHoveredElement({
              type: 'educacion',
              data: info.object.properties
            });
            setHoveredFoco(null);
          } else {
            setHoveredElement(null);
          }
          return false;
        }
      }));
      newLayers.push(new IconLayer({
        id: 'educacion_icon',
        data: educacionLayer.data['features'],
        iconAtlas: cityIOSettings.iconsURL + '/pin_cap.png',
        iconMapping: {
          marker: { x: 128, y: 0, width: 128, height: 128, mask: true }
        },
        getPixelOffset: [0, -3],
        getIcon: d => 'marker',
        sizeMinPixels: 16,
        sizeMaxPixels: 16,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 255, 255],
        parameters: {
          depthTest: false
        },
        visible: educacionLayer.visible
      }));
    }

    // Salud
    const saludLayer = layers.find(l => l.id === 'salud')
    if(saludLayer?.data) {
      newLayers.push(new IconLayer({
        id: 'salud-pin',
        data: saludLayer.data['features'],
        pickable: true,
        iconAtlas: cityIOSettings.iconsURL + '/pin_cross.png',
        iconMapping: {
          marker: { x: 0, y: 0, width: 128, height: 128, mask: true }
        },
        getIcon: d => 'marker',
        sizeScale: d => hoveredElement?.type === 'salud' && 
                   hoveredElement?.data === d.properties ? 1.3 : 1,
        sizeMinPixels: 32,
        sizeMaxPixels: 32,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 128, 128],
        parameters: {
          depthTest: false
        },
        visible: saludLayer.visible,
        autoHighlight: false,
        onHover: (info) => {
          if (info.object) {
            setHoveredElement({
              type: 'salud',
              data: info.object.properties
            });
            setHoveredFoco(null);
          } else {
            setHoveredElement(null);
          }
          return false;
        }
      }));
      newLayers.push(new IconLayer({
        id: 'salud_icon',
        data: saludLayer.data['features'],
        iconAtlas: cityIOSettings.iconsURL + '/pin_cross.png',
        iconMapping: {
          marker: { x: 128, y: 0, width: 128, height: 128, mask: true }
        },
        getPixelOffset: [0, -3],
        getIcon: d => 'marker',
        sizeMinPixels: 16,
        sizeMaxPixels: 16,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 255, 255],
        parameters: {
          depthTest: false
        },
        visible: saludLayer.visible
      }));
    }

    // Área de cobertura de focos (polígono rojo semitransparente)
    const areaFocosLayer = layers.find(l => l.id === 'area_focos');
    if(areaFocosLayer?.data) {
      newLayers.push(new GeoJsonLayer({
        id: 'area_focos',
        data: areaFocosLayer.data,
        filled: true,
        stroked: true,
        getFillColor: [255, 0, 0, 24], // Rojo semitransparente (40/255 ≈ 0.16)
        getLineColor: [255, 0, 0, 255], // Borde rojo opaco
        getLineWidth: 4,
        lineWidthMinPixels: 3,
        getDashArray: [10, 10], // Línea entrecortada
        dashJustified: true,
        extensions: [new PathStyleExtension({ dash: true })],
        visible: areaFocosLayer.visible,
      }));
    }

    // Focos
    const focosLayer = layers.find(l => l.id === 'focos')
    if(focosLayer?.data) {
      // Calcular threshold lineal basado en zoom
      // Interpolación lineal: zoom más bajo = threshold más alto (solo focos densos)
      // Zoom 7: threshold 90 (solo los más densos)
      // Zoom 15: threshold 0 (todos los focos)
      const calculateFilterThreshold = (zoom) => {
        const minZoom = 10.0;   // Zoom mínimo
        const maxZoom = 12.0;  // Zoom máximo
        const maxThreshold = 90.0; // Threshold en zoom mínimo (solo muy densos)
        const minThreshold = 0.0;  // Threshold en zoom máximo (todos)
        
        // Interpolación lineal
        const normalizedZoom = Math.max(0.0, Math.min(1.0, (zoom - minZoom) / (maxZoom - minZoom)));
        const threshold = maxThreshold - (normalizedZoom * (maxThreshold - minThreshold));
        
        return Math.max(minThreshold, Math.min(maxThreshold, threshold));
      };
      
      const threshold = calculateFilterThreshold(zoomLevel);
      const filterRange = [threshold, 100];
      
      newLayers.push(new IconLayer({
        id: 'focos-pin',
        data: focosLayer.data['features'],
        pickable: true,
        iconAtlas: cityIOSettings.iconsURL + '/pin_flame.png',
        iconMapping: {
          marker: { x: 0, y: 0, width: 128, height: 128, mask: true }
        },
        getIcon: d => 'marker',
        sizeMinPixels: 32,
        sizeMaxPixels: 32,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 32, 32],
        parameters: {
          depthTest: false
        },
        // DataFilterExtension para filtrar por densidad de vecinos
        getFilterValue: d => d.properties.density_score_normalized || 0,
        filterRange: filterRange,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        visible: focosLayer.visible,
        autoHighlight: false,
        onHover: (info, event) => {
          // No prevenir eventos por defecto para permitir scroll
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
          
          if (info.object) {
            const currentFocoId = info.object.properties?.id || info.index;
            const previousFocoId = previousFocoRef.current?.properties?.id || previousFocoRef.current?.index;
            
            if (currentFocoId !== previousFocoId) {
              hoverTimeoutRef.current = setTimeout(() => {
                setHoveredFoco(info.object);
                previousFocoRef.current = info.object;
              }, 50);
            }
          } else {
            if (previousFocoRef.current !== null) {
              setHoveredFoco(null);
              previousFocoRef.current = null;
            }
          }
          
          return false; // No consume el evento, permite propagación
        }
      }));
      newLayers.push(new IconLayer({
        id: 'focos_icon',
        data: focosLayer.data['features'],
        iconAtlas: cityIOSettings.iconsURL + '/pin_flame.png',
        iconMapping: {
          marker: { x: 128, y: 0, width: 128, height: 128, mask: true }
        },
        getPixelOffset: [0, -3],
        getIcon: d => 'marker',
        sizeMinPixels: 16,
        sizeMaxPixels: 16,
        getPosition: d => d.geometry.coordinates,
        getColor: [255, 255, 255],
        parameters: {
          depthTest: false
        },
        // Mismo filtro para el ícono
        getFilterValue: d => d.properties.density_score_normalized || 0,
        filterRange: filterRange,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        visible: focosLayer.visible
      }));
    }
    
    return newLayers;
  };

  return (
    <>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <StatsPanel stats={affectedStats} element={hoveredElement} />
        <DeckglBase
          layers={renderDeckLayers()}
          setDeckGLRef={setDeckGLRef}
          onViewStateChange={({ viewState }) => {
            setZoomLevel(viewState.zoom);
            setCurrentViewState(viewState);
          }}
          parameters={{
            clearColor: [0, 0, 0, 0], // Example: a light beige background color
          }}
        />
      </div>
    </>
  );
}
